import { Box, CircularProgress, Skeleton, Stack, SxProps, TablePagination, Typography } from '@mui/material';
import React, {useCallback, useMemo} from "react";
import { SortDirection } from '@coverright/data-access/types/enrollment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { SortAscending, SortDescending } from '@phosphor-icons/react';

export interface DataGridProps<T, S> {
  loading?: boolean;
  columns: DataGridColumn<T, S>[];
  rows: T[];
  extraRowBuilder?: (row: T, i: number) => React.ReactNode;
  getRowClassName?: (row: T, i: number) => string;
  onRowMouseEnter?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: T, i: number) => void;
  onRowMouseLeave?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: T, i: number) => void;
  rowsPerPage: number;
  page: number;
  total: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (value: number) => void;
  onRowClick?: (row: T) => void;
  hidePaginator?: boolean;
  error?: string;
  sx?: SxProps;
  sort?: DataGridSortModel<S>;
  onSortModelChange?: (model?: DataGridSortModel<S>) => void;
  rowsPerPageOptions?: number[],
}

export function DataGrid<T, S>(props: DataGridProps<T, S>) {
  const {
    loading,
    columns,
    sx,
    rowsPerPage,
    page,
    hidePaginator,
    onPageChange,
    onRowsPerPageChange,
    total,
    rows,
    onRowClick,
    error,
    extraRowBuilder,
    getRowClassName,
    onRowMouseEnter,
    onRowMouseLeave,
    sort,
    onSortModelChange,
    rowsPerPageOptions = [20, 50, 100]
  } = props;

  const tableStyles: SxProps = useMemo(() => {
    return sx ? {...styles, ...sx} as SxProps : styles
  }, [sx]);

  const onSortClick = useCallback((field: S) => {
    if (onSortModelChange) {
      if (sort && sort.field === field) {
        onSortModelChange(sort.direction === SortDirection.Desc ? undefined : {field, direction: SortDirection.Desc});
        return;
      }
      onSortModelChange({field, direction: SortDirection.Asc})
    }
  }, [sort, onSortModelChange])

  return <>
    <OverlayScrollbarsComponent
      options={{ paddingAbsolute: true, scrollbars: {autoHide: 'move'} }}
      style={{paddingRight: 8, paddingBottom: 16}}
      defer
    >
      {(!loading && !rows.length) && (
        <Box sx={{display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center', width: 1, height: 450, borderRadius: '8px', border: '1px solid rgb(228, 228, 231)'}}>
          {!error && <img src={'/assets/no-data.png'} width={200} height={'auto'} />}
          {!!error && <Typography>An error occurred while fetching data</Typography>}
        </Box>
      )}

      {(loading || !!rows.length) && (
        <Box sx={tableStyles} component={'table'} cellSpacing={0}>
          <thead>
          <tr>
            {columns.map((c, i) => <Box component={'td'}
                                        sx={{
                                          cursor: c.sortKey ? 'pointer' : 'default',
                                          '& .sort-icon': {
                                            opacity: c.sortKey === sort?.field ? 1 : 0
                                          },
                                          '&:hover .sort-icon': {
                                            opacity: c.sortKey === sort?.field ? 1 : .5,
                                          },
                                          width: c.width
                                        }}
                                        onClick={() => c.sortKey && onSortClick(c.sortKey)}
                                        key={'col' + i}>
              <Stack direction={'row'} alignItems={'center'} gap={.5}>
                {c.title}
                {c.sortKey && <SortIcon direction={sort?.field === c.sortKey ? sort?.direction : undefined}/>}
              </Stack>
            </Box>)}
          </tr>
          </thead>
          <tbody>
          {!rows.length && loading && Array(rowsPerPage > 100 ? 20 : rowsPerPage).fill(1).map((row, i) => (
            <tr key={'row' + i}>
              {columns.map((c, j) => (
                <Box component={'td'} sx={{ width: c.width, minWidth: c.width || 150 }} key={'row' + c.title + j}>
                  <Skeleton variant={'text'} width={'100%'} />
                </Box>
              ))}
            </tr>
          ))}
          {rows.map((row, i) => <React.Fragment key={'row' + i}>
            <tr onClick={() => onRowClick && onRowClick(row)}
                className={getRowClassName ? getRowClassName(row, i) : undefined}
                onMouseEnter={e => onRowMouseEnter && onRowMouseEnter(e, row, i)}
                onMouseLeave={e => onRowMouseLeave && onRowMouseLeave(e, row, i)}
            >
              {columns.map((c, j) => (
                <Box component={'td'} sx={{ width: c.width, cursor: onRowClick ? 'pointer' : undefined }} key={'row' + c.title + j}>
                  {c.value(row)}
                </Box>
              ))}
            </tr>
            {extraRowBuilder && extraRowBuilder(row, i)}
          </React.Fragment>)}
          </tbody>
        </Box>
      )}

    </OverlayScrollbarsComponent>

      {(loading || !!rows.length) && !hidePaginator && <Stack direction='row' alignItems={'center'} justifyContent={'flex-end'}>
        {loading && <CircularProgress color={'inherit'} size={15} />}
        <TablePagination
          component="div"
          sx={{overflow: 'visible'}}
          count={typeof total === 'string' ? parseFloat(total) : total || 0}
          page={page}
          showFirstButton
          showLastButton
          onPageChange={(e, page) => onPageChange && onPageChange(page)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          onRowsPerPageChange={e => onRowsPerPageChange && onRowsPerPageChange(parseFloat(e.target.value))}
        />
      </Stack>}


  </>

}

const SortIcon = ({direction}: {direction?: SortDirection}) => (
  direction?.toLowerCase() === SortDirection.Desc ?
    <SortDescending className={'sort-icon'} size={16} color="#1c434f" /> :
    <SortAscending className={'sort-icon'} size={16} color="#1c434f" />
)

export interface DataGridSortModel<S> {
  field: S,
  direction: SortDirection
}

export interface DataGridColumn<T, S> {
  title: string,
  value: (v: T) => React.ReactNode,
  sortKey?: S,
  width?: number | string,
}

const styles: SxProps = {
  width: 1,
  borderBottom: '1px solid rgb(228, 228, 231)',
  borderRight: '1px solid rgb(228, 228, 231)',

  '& td': {
    whiteSpace: 'nowrap',
    px: '10px',
    verticalAlign: 'center',
    height: 40,
    '&:first-of-type': {
      position: 'sticky',
      left: 0,
      zIndex: 1,
      borderLeft: '1px solid rgb(228, 228, 231)',
    }
  },
  '& tr td:not(:last-of-type)': {
    borderRight: '1px solid rgb(228, 228, 231)',
  },
  '& tr:not(:last-of-type) td': {
    borderBottom: '1px solid rgb(228, 228, 231)',
  },
  '& tbody td': {
    fontSize: 14,
    fontWeight: 500,
  },
  '& tbody tr:not(.info)': {
    '&:nth-of-type(odd) td': {
      backgroundColor: '#fff',
    },
    '&:nth-of-type(even) td': {
      backgroundColor: '#f6f7f7',
    }
  },
  '& tbody tr:not(.info):hover td': {
    backgroundColor: '#eef8fd',
  },
  '& thead tr': {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    '& td': {
      verticalAlign: 'middle',
      backgroundColor: '#fff',
      borderTop: '1px solid rgb(228, 228, 231)',
      borderBottom: '1px solid rgb(228, 228, 231)',
      fontSize: 12,
      color: 'rgb(94, 94, 100)',
      '&:not(:first-of-type)': {
        zIndex: 3,
      }
    }
  }
}
